import { useState } from "react";
import { createPortal } from "react-dom";
import {
  putApproveCompanyCreditRequest,
  putRejectCompanyCreditRequest,
  putUpdateCreditLimit,
  useGetCompanyCreditRequest,
} from "@/services/Company";
import { Company, CompanyPendingAction } from "@/types";
import { Grid, Group, Stack } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";

import NumberInput from "@/components/atoms/NumberInput";
import NumberFormatter from "@/components/atoms/NumberFormatter";
import Text from "@/components/atoms/Text";
import CTA from "@/components/atoms/CTA";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import Textarea from "@/components/atoms/Textarea";
import Modal, { ModalContent, ModalCTAs } from "@/components/molecules/Modal";
import CreditHistoryTable from "./CreditHistoryTable";
import styles from "./styles.module.css";

interface CreditTabProps {
  company: Company;
  onCloseDrawer: () => void;
  hasPendingCreditAction: CompanyPendingAction | undefined;
}

export default function CreditTab({ company, onCloseDrawer, hasPendingCreditAction }: CreditTabProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [creditLimit, setCreditLimit] = useState<number>(company.credit.creditLimit);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const { mutate: updateCreditLimit, isPending: isUpdatingCreditLimit } = useMutation({
    mutationFn: () => putUpdateCreditLimit({ companyid: company.id, creditLimit }),
    onSuccess: async () => {
      setConfirmModalOpen(false);
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:credit.creditLimitUpdated"))
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["getCompanyById", company.id] }),
        queryClient.invalidateQueries({ queryKey: ["getCompaniesForAdmin"] }),
        queryClient.invalidateQueries({ queryKey: ["getCompanyCreditHistory", company.id] }),
      ]);
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), t("notifications:credit.creditLimitUpdateError"))
      );
    },
  });

  return (
    <>
      {hasPendingCreditAction && company.credit && (
        <CreditApplicationBanner company={company} onCloseDrawer={onCloseDrawer} />
      )}

      {createPortal(
        <Modal opened={confirmModalOpen} onClose={() => setConfirmModalOpen(false)} title={t("common:creditLimit")}>
          <ModalContent>
            <Stack gap="md" p="20px 23px">
              <Text>{t("commonAdmin:credit.confirmCreditLimitChange")}</Text>
              <Group>
                <Text size="sm" color="secondary">
                  <NumberFormatter value={company.credit.creditLimit} suffix={` ${company.defaultCurrency}`} />
                </Text>
                <SVGAsset name="arrowGrayUp" style={{ rotate: "90deg" }} />
                <Text size="sm">
                  <NumberFormatter value={creditLimit} suffix={` ${company.defaultCurrency}`} />
                </Text>
              </Group>
            </Stack>
          </ModalContent>
          <ModalCTAs>
            <CTA intent="secondary" onClick={() => setConfirmModalOpen(false)}>
              {t("actions:cancel")}
            </CTA>
            <CTA onClick={() => updateCreditLimit()} loading={isUpdatingCreditLimit}>
              {t("actions:save")}
            </CTA>
          </ModalCTAs>
        </Modal>,
        document.body
      )}

      <Grid p="30px 20px 30px 20px" align="center">
        <GridRow
          label={t("commonAdmin:credit.recommendedCreditLimitDUNS")}
          value={
            <NumberFormatter
              value={company.credit.recommendedCreditlimitDUNS ?? undefined}
              suffix={` ${company.defaultCurrency}`}
            />
          }
        />

        <Grid.Col span={4}>
          <Text color="secondary" size="sm">
            {t("common:creditLimit")}
          </Text>
        </Grid.Col>
        <Grid.Col span={5}>
          <NumberInput
            value={creditLimit}
            onChange={(val) => setCreditLimit(val as number)}
            suffix={` ${company.defaultCurrency}`}
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <CTA size="sm" onClick={() => setConfirmModalOpen(true)}>
            {t("actions:save")}
          </CTA>
        </Grid.Col>

        <GridRow
          label={t("common:remainingCredit")}
          value={<NumberFormatter value={company.credit.remainingCredit} suffix={` ${company.defaultCurrency}`} />}
        />
      </Grid>

      <Stack gap="sm" p="0 20px 30px 20px">
        <Text size="sm" color="secondary">
          {t("commonAdmin:credit.creditHistory")}
        </Text>
        <CreditHistoryTable companyId={company.id} currency={company.defaultCurrency} />
      </Stack>
    </>
  );
}

const GridRow = ({
  label,
  value,
  style,
}: {
  label: string;
  value: string | React.ReactNode | null;
  style?: React.CSSProperties;
}) => {
  return (
    <>
      <Grid.Col span={4}>
        <Text color="secondary" size="sm">
          {label}
        </Text>
      </Grid.Col>
      <Grid.Col span={8}>
        <Text size="sm" style={style}>
          {value ?? "-"}
        </Text>
      </Grid.Col>
    </>
  );
};

const CreditApplicationBanner = ({ company, onCloseDrawer }: { company: Company; onCloseDrawer: () => void }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [comment, setComment] = useState("");

  const { data: creditRequest } = useGetCompanyCreditRequest(company.id);
  // A pending request the backend has no amount for (204) can only be rejected.
  const isRequestedAmountMissing = creditRequest === null;

  const { mutateAsync: mutateRejectCredit, isPending: isRejectCreditPending } = useMutation({
    mutationFn: async () => {
      await putRejectCompanyCreditRequest(company.id, comment);
    },
    onSuccess: () => {
      setComment("");
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.success"),
          t("notifications:creditApplication.applicationRejectedSuccess")
        )
      );
      queryClient.invalidateQueries({ queryKey: ["getCompaniesForAdmin"] });
      queryClient.invalidateQueries({ queryKey: ["getCompanyCreditHistory", company.id] });
      queryClient.invalidateQueries({ queryKey: ["getCompanyCreditRequest", company.id] });
      onCloseDrawer();
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(
          t("notifications:generic.error"),
          t("notifications:creditApplication.applicationRejectedError")
        )
      );
    },
  });

  const { mutateAsync: mutateApproveCreditRequest, isPending: isApproveCreditPending } = useMutation({
    mutationFn: async () => {
      await putApproveCompanyCreditRequest(company.id, comment);
    },
    onSuccess: () => {
      setComment("");
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.success"),
          t("notifications:creditApplication.applicationApprovedSuccess")
        )
      );
      queryClient.invalidateQueries({ queryKey: ["getCompaniesForAdmin"] });
      queryClient.invalidateQueries({ queryKey: ["getCompanyById", company.id] });
      queryClient.invalidateQueries({ queryKey: ["getCompanyCreditHistory", company.id] });
      queryClient.invalidateQueries({ queryKey: ["getCompanyCreditRequest", company.id] });
      onCloseDrawer();
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(
          t("notifications:generic.error"),
          t("notifications:creditApplication.applicationApprovedError")
        )
      );
    },
  });
  return (
    <Stack className={styles.banner} p={"32px 20px 32px 20px"}>
      <Text size="xl">{company.companyName}</Text>
      <Group>
        <Text size="xl" color="secondary">
          <NumberFormatter value={company.credit.creditLimit} suffix={` ${company.defaultCurrency}`} />
        </Text>
        <SVGAsset name="arrowGrayUp" style={{ rotate: "90deg" }} />
        <Text size="xl" color="secondary">
          {isRequestedAmountMissing ? (
            "-"
          ) : (
            <NumberFormatter value={creditRequest?.creditLimit} suffix={` ${company.defaultCurrency}`} />
          )}
        </Text>
      </Group>
      {isRequestedAmountMissing && (
        <Text size="sm" color="warning">
          {t("commonAdmin:credit.requestedAmountUnavailable")}
        </Text>
      )}

      <Grid>
        <Grid.Col span={8}>
          <Textarea label={t("common:comment")} value={comment} onChange={(e) => setComment(e.target.value)} />
        </Grid.Col>
      </Grid>

      <Group justify="flex-end" p="20px">
        <CTA
          intent="secondary"
          onClick={() => mutateRejectCredit()}
          loading={isRejectCreditPending || isApproveCreditPending}
        >
          {t("actions:reject")}
        </CTA>
        <CTA
          onClick={() => mutateApproveCreditRequest()}
          disabled={!creditRequest}
          loading={isRejectCreditPending || isApproveCreditPending}
        >
          {t("actions:approve")}
        </CTA>
      </Group>
    </Stack>
  );
};
